import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Clock, AlertCircle, Loader2, ChevronLeft, ChevronRight, QrCode } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import api from "../../lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function pad(n) { return String(n).padStart(2, "0"); }

export default function MemberAttendance() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const getLocalDateString = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [selectedDate, setSelectedDate] = useState(getLocalDateString(new Date()));

  // Calendar navigation
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth()); // 0-based

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) { navigate("/"); return; }
      try {
        const res = await api.get("/member-portal/attendance", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRecords(res.data.attendance || []);
      } catch (err) {
        if (err?.response?.status === 401) { navigate("/"); return; }
        setError("Failed to load attendance.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, refreshTrigger]);

  useEffect(() => {
    if (!scannerOpen) return;

    if (!window.isSecureContext) {
      toast.error("Camera access requires a secure connection (HTTPS or localhost).");
      setScannerOpen(false);
      return;
    }

    let html5QrCode;
    const timer = setTimeout(() => {
      const element = document.getElementById("member-qr-reader");
      if (!element) return;

      html5QrCode = new Html5Qrcode("member-qr-reader");

      const successCallback = async (decodedText) => {
        try {
          await html5QrCode.stop();
        } catch (e) {
          console.error("Failed to stop scanner:", e);
        }
        handleQrScanSuccess(decodedText);
      };

      const errorCallback = () => { };

      // Dynamic qrbox sizing function to ensure a large, responsive square scanner area on all devices
      const qrboxConfig = (viewfinderWidth, viewfinderHeight) => {
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        // Make the scan box 80% of the smaller edge, ensuring it's easy to align the QR code
        const size = Math.floor(minEdge * 0.8);
        return { width: size, height: size };
      };

      const startWithFacingMode = () => {
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: qrboxConfig },
            successCallback,
            errorCallback
          )
          .catch((err) => {
            console.warn("Environment camera failed, trying user camera:", err);
            html5QrCode
              .start(
                { facingMode: "user" },
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback
              )
              .catch((err2) => {
                console.error("Both camera options failed:", err2);
                toast.error("Failed to access camera. Please check permissions.");
                setScannerOpen(false);
              });
          });
      };

      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length > 0) {
            let cameraId = devices[0].id;
            const backCamera = devices.find((device) =>
              device.label.toLowerCase().includes("back") ||
              device.label.toLowerCase().includes("rear") ||
              device.label.toLowerCase().includes("environment")
            );
            if (backCamera) {
              cameraId = backCamera.id;
            }
            html5QrCode
              .start(
                cameraId,
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback
              )
              .catch((err) => {
                console.warn("Failed to start camera by ID, falling back to facingMode:", err);
                startWithFacingMode();
              });
          } else {
            startWithFacingMode();
          }
        })
        .catch((err) => {
          console.warn("getCameras failed, trying facingMode direct start:", err);
          startWithFacingMode();
        });
    }, 500);

    return () => {
      clearTimeout(timer);
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode
          .stop()
          .then(() => {
            html5QrCode.clear();
          })
          .catch((err) => console.error("Scanner cleanup error:", err));
      }
    };
  }, [scannerOpen]);

  const handleQrScanSuccess = async (decodedText) => {
    setScannerOpen(false);

    let orgId = "";
    let branchId = "";

    const urlMatch = decodedText.match(/\/public\/attendance\/([^/]+)\/([^/]+)/);
    if (urlMatch) {
      orgId = urlMatch[1];
      branchId = urlMatch[2];
    } else {
      const parts = decodedText.split(":");
      if (parts[0] === "ATTENDANCE" && parts.length >= 3) {
        orgId = parts[1];
        branchId = parts[2];
      }
    }

    if (!orgId || !branchId) {
      toast.error("Invalid QR Code. Please scan a valid gym check-in QR code.");
      return;
    }

    const member = JSON.parse(localStorage.getItem("member_data") || "{}");
    if (!member?.phone) {
      toast.error("Member details not found. Please login again.");
      return;
    }

    toast.loading("Processing check-in...");
    try {
      const res = await api.post("/attendance/public-checkin", {
        phone: member.phone,
        organizationId: orgId,
        branchId: branchId,
      });

      toast.dismiss();
      toast.success(res.data?.message || "Check-in successful!");
      setSelectedDate(getLocalDateString(new Date()));
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "Check-in failed.");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
      <AlertCircle className="h-8 w-8" />
      <p>{error}</p>
    </div>
  );

  // Build a Set of attended date strings YYYY-MM-DD for O(1) lookup
  const attendedDates = new Set(
    records.map((r) => {
      const raw = r.date || "";
      return raw.length >= 10 ? raw.slice(0, 10) : "";
    }).filter(Boolean)
  );

  // Stats
  const thisMonthKey = `${calYear}-${pad(calMonth + 1)}`;
  const thisMonthCount = records.filter((r) => (r.date || "").startsWith(thisMonthKey)).length;
  const totalCount = records.length;

  // Calendar grid for current calYear/calMonth
  const firstDay = new Date(calYear, calMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const cells = Array(firstDay).fill(null)
    .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
  // pad to complete rows
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };
  const isToday = (d) =>
    d && calYear === today.getFullYear() && calMonth === today.getMonth() && d === today.getDate();

  // Recent 30 records for the list
  const recent = [...records].slice(0, 30);

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-2xl font-black font-heading uppercase tracking-wide">Attendance</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Your gym check-in history</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">This Month</p>
                <p className="text-3xl font-black font-heading text-primary">{thisMonthCount}</p>
                <p className="text-xs text-muted-foreground mt-1">check-ins</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-primary">
                <CalendarCheck className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Total</p>
                <p className="text-3xl font-black font-heading text-primary">{totalCount}</p>
                <p className="text-xs text-muted-foreground mt-1">all time</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-primary">
                <Clock className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar heatmap */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
              {MONTHS[calMonth]} {calYear}
            </CardTitle>
            <div className="flex items-center gap-1">
              <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-mono uppercase text-muted-foreground py-1">{d}</div>
            ))}
          </div>
          {/* Date cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={idx} />;
              const dateStr = `${calYear}-${pad(calMonth + 1)}-${pad(day)}`;
              const attended = attendedDates.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const selectedCls = isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background z-10" : "";
              const todayCls = isToday(day) ? "border border-primary font-bold" : "";
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedDate(dateStr)}
                  title={attended ? `Attended on ${dateStr}` : dateStr}
                  className={`aspect-square flex items-center justify-center rounded-md text-xs font-medium transition-all cursor-pointer hover:opacity-90 active:scale-95 ${attended
                    ? "bg-primary text-primary-foreground font-bold"
                    : "bg-secondary text-muted-foreground"
                    } ${todayCls} ${selectedCls}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 justify-end">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-primary" /> Attended
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-secondary" /> Absent
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">Day Information</CardTitle>
          <span className="text-sm font-semibold text-primary">
            {(() => {
              const [y, m, d] = selectedDate.split("-").map(Number);
              const localDate = new Date(y, m - 1, d);
              return localDate.toLocaleDateString("en-IN", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric"
              });
            })()}
          </span>
        </CardHeader>
        <CardContent>
          {(() => {
            const selectedRecord = records.find(r => r.date && r.date.slice(0, 10) === selectedDate);
            if (selectedRecord) {
              const checkInTime = selectedRecord.checkIn
                ? new Date(selectedRecord.checkIn).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                : null;
              const checkOutTime = selectedRecord.checkOut
                ? new Date(selectedRecord.checkOut).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                : null;
              return (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10 animate-in fade-in duration-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 animate-pulse">
                      <CalendarCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Present</p>
                      <p className="text-xs text-muted-foreground">{selectedRecord.branchName || "Gym Branch"}</p>
                    </div>
                  </div>
                  <div className="flex gap-6 sm:text-right">
                    {checkInTime && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Check-in</p>
                        <p className="text-base font-mono font-bold text-primary">{checkInTime}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Check-out</p>
                      <p className="text-base font-mono font-bold text-primary">
                        {checkOutTime ? (
                          checkOutTime
                        ) : (
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Active</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30 border border-border/40 animate-in fade-in duration-200">
                  <div className="w-10 h-10 rounded-full bg-muted/20 flex items-center justify-center text-muted-foreground">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Absent / No Record</p>
                    <p className="text-xs text-muted-foreground">No attendance was logged on this day.</p>
                  </div>
                </div>
              );
            }
          })()}
        </CardContent>
      </Card>

      {/* Recent list */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">Recent Check-ins</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
              <CalendarCheck className="h-8 w-8 opacity-40" />
              <p className="text-sm">No attendance records yet</p>
            </div>
          ) : (
            <div className="space-y-0">
              {recent.map((r, i) => {
                const dateObj = r.date ? new Date(r.date) : null;
                const dateLabel = dateObj
                  ? dateObj.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
                  : r.date;
                const checkInTime = r.checkIn
                  ? new Date(r.checkIn).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                  : null;
                const checkOutTime = r.checkOut
                  ? new Date(r.checkOut).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                  : null;
                return (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <CalendarCheck className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{dateLabel}</p>
                        {r.branchName && (
                          <p className="text-xs text-muted-foreground">{r.branchName}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-0.5">
                      {checkInTime && (
                        <div className="text-xs text-muted-foreground">
                          In: <span className="text-sm font-semibold text-primary">{checkInTime}</span>
                        </div>
                      )}
                      {checkOutTime && (
                        <div className="text-xs text-muted-foreground">
                          Out: <span className="text-sm font-semibold text-primary">{checkOutTime}</span>
                        </div>
                      )}
                      {!checkInTime && !checkOutTime && (
                        <span className="text-sm font-medium text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Floating Action Button */}
      <button
        onClick={() => setScannerOpen(true)}
        className="fixed bottom-24 right-6 lg:bottom-10 lg:right-10 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all duration-200 border border-primary/20 flex items-center justify-center"
        title="Scan QR Code to Check In"
        data-testid="attendance-scan-fab"
      >
        <QrCode className="h-6 w-6" />
      </button>

      {/* QR Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
        <DialogContent className="max-w-md w-[90%] rounded-lg border border-border bg-card p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Scan Gym QR Code</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              Position the gym entrance QR code within the scanner viewport to check in or out.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center mt-4">
            <div
              id="member-qr-reader"
              className="w-full aspect-square overflow-hidden rounded-md bg-black relative border border-border"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
